import { Alert, Button, Card, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  student_id: string;
  section_id?: string;
  category: string;
  summary: string;
  detail_text?: string;
  effective_from: dayjs.Dayjs;
  effective_to?: dayjs.Dayjs;
};

export default function InclusionAccommodationFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/inclusion/accommodations', {
        student_id: values.student_id,
        section_id: values.section_id || undefined,
        category: values.category,
        summary: values.summary,
        detail_text: values.detail_text,
        effective_from: values.effective_from.format('YYYY-MM-DD'),
        effective_to: values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: (res) => {
      message.success(t('accommodationCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/inclusion/accommodations/${id}` : '/education/inclusion/accommodations');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newAccommodation')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newAccommodation')}
      description={t('inclusionAccommodationsIntro')}
      extra={
        <Button onClick={() => navigate('/education/inclusion/accommodations')}>{t('inclusionAccommodations')}</Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('inclusionNonDiagnosticHint')} />
        <Form
          layout="vertical"
          initialValues={{ effective_from: dayjs(), category: 'extra_time' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="section_id" label={t('section')}>
            <EducationSectionSelect allowClear />
          </Form.Item>
          <Form.Item name="category" label={t('category')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'extra_time', label: t('accCategoryExtraTime') },
                { value: 'seating', label: t('accCategorySeating') },
                { value: 'scribe', label: t('accCategoryScribe') },
                { value: 'reader', label: t('accCategoryReader') },
                { value: 'assistive_tech', label: t('accCategoryAssistive') },
                { value: 'break_schedule', label: t('accCategoryBreak') },
                { value: 'curriculum_adjustment', label: t('accCategoryCurriculum') },
                { value: 'other', label: t('accCategoryOther') },
              ]}
            />
          </Form.Item>
          <Form.Item name="effective_from" label={t('effectiveFrom')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="effective_to" label={t('effectiveTo')}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="summary" label={t('summary')} rules={[{ required: true }]}>
            <Input maxLength={500} />
          </Form.Item>
          <Form.Item name="detail_text" label={t('detail')}>
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
